<template>
    <div class="bg-white mb-3">
        <div class="d-flex justify-content-between align-items-center">
            <div>
                <h1 class="h4 mb-0">Order Remake</h1>
            </div>
        </div>
        <Flash v-if="flashMessage" alert="danger">{{ flashMessage }}</Flash>
        <div class="d-flex flex-wrap section-container mb-4 pb-5">
            <div v-for="(section, index) of sections" v-bind:key="section.name" class="section mt-3" :class="{ 'section-active': index <= currentSectionIndex }">
                <span class="progress-circle">{{index + 1}}</span>
                <div>
                    {{section.label}}
                </div>
            </div>
        </div>
    </div>
    
    <form enctype="multipart/form-data" novalidate class="bg-white" @submit.prevent="submitOrderRemake">
        <div v-if="currentSectionName == 'impressions'">
        <div class="row">
            <div class="col-12 col-md-4 mb-3">
                <button class="impression-type-button btn btn-primary btn-block" type="button" @click="impressionTypeButton(2)">
                    <strong class="label">Physical impressions</strong>
                    <br />
                    (Alginate, silicone, etc)
                </button>
            </div>
            <div class="col-12 col-md-4 mb-3">
                <button class="impression-type-button btn btn-primary btn-block" type="button" @click="impressionTypeButton(1)">
                    <strong class="label">Digital impressions</strong>
                    <br />
                    (Intraoral scanner)
                </button>
            </div>
            <div class="col-12 col-md-4 mb-3">
                <button class="impression-type-button btn btn-primary btn-block" type="button" @click="impressionTypeButton(2)">
                    <strong class="label">No impressions</strong>
                    <br />
                    (Pre-cast or without impressions)
                </button>
            </div>
        </div>
        <div><strong>Note:</strong> No separate lab ticket is required. A lab ticket will be generated for you (if required) once your order has been requested.</div>
    </div>
    <div v-if="currentSectionName == 'scans'">
        <div class="mb-2">
            <FileInput
                label="Scans"
                v-model="scansSection.scans"
                :v="v$.scansSection.scans"
                :allowedExtensions="['ply', 'stl']"
            />
        </div>
        <div class="buttons">
            <button class="btn btn-primary me-1" type="button" @click="nextButton">Next</button>
            <button class="btn btn-secondary me-1" type="button" @click="previousButton">Previous</button>
            <router-link class="btn btn-light" to="/orders">Cancel</router-link>
        </div>
    </div>
    <!-- account section -->
    <div v-if="currentSectionName == 'account'">
        <div class="row">
            <div class="col-md-6 mb-2">
                <DropdownInput
                    label="Account"
                    v-model="accountSection.account_id"
                    :v="v$.accountSection.account_id"
                    :options="accounts"
                />
            </div>
        </div>
        <div class="buttons">
            <button class="btn btn-primary me-1" type="button" @click="nextButton">Next</button>
            <button class="btn btn-secondary me-1" type="button" @click="previousButton">Previous</button>
            <router-link class="btn btn-light" to="/orders">Cancel</router-link>
        </div>
    </div>
    <!-- account seciton ends -->
    <!-- patient/basic section -->
    <div v-if="currentSectionName == 'basic'">
        <div class="row">
            <div class="col-sm-4 mb-2">
                <TextInput
                    label="Patient reference"
                    v-model="basicSection.patient_reference"
                    :v="v$.basicSection.patient_reference"
                />
                <div style="color: #737373">{{preferences.patientExplainer}}</div>
            </div>
            <div class="col-sm-5 col-md-4 mb-2">
                <DropdownInput
                    label="Service level"
                    v-model="basicSection.standard"
                    :v="v$.basicSection.standard"
                    :options="standards"
                />
            </div>
            <div v-if="isEconomyEnabled" class="col-sm-5 col-md-4 mb-2">
                <DropdownInput
                    label="Price Tier"
                    v-model="basicSection.pricing_tier"
                    :v="v$.basicSection.pricing_tier"
                    :options="pricing_tiers"
                />
            </div>
            <div v-if="accounts.find(a => a.id == accountSection.account_id && true == showReferralCode)" class="col-sm-4 mb-2">
                <TextInput
                    label="Referral Code (Optional)"
                    v-model="basicSection.referral_code"
                    :v="v$.basicSection.referral_code"
                />
            </div>
        </div>
        <div class="buttons">
            <button class="btn btn-primary me-1" type="button" @click="nextButton">Next</button>
            <button class="btn btn-secondary me-1" type="button" @click="previousButton">Previous</button>
            <router-link class="btn btn-light" to="/orders">Cancel</router-link>
        </div>
    </div>
    <!-- patient/basic section ends -->
    <!-- products section begins -->
    <div v-if="currentSectionName == 'products'">
        <div class="mb-2">
            <ProductInput
                v-model="productsSection.products"
                :v="v$.productsSection.products"
                :service_level="basicSection.standard"
                :accountId="this.accountSection.account_id"
                :impressionType="this.impressionType"
                :pricing_tier="this.basicSection.pricing_tier"
                :order_id="null"
                ref="remakeProduct"
                :displayPopup="false"
            />
        </div>
        <div class="buttons">
            <button class="btn btn-primary me-1" type="button" @click="nextButton">Next</button>
            <button class="btn btn-secondary me-1" type="button" @click="previousButton">Previous</button>
            <router-link class="btn btn-light" to="/orders">Cancel</router-link>
        </div>
    </div>
    <!-- products section ends -->
    <!-- instruction section begins-->
    <div v-if="currentSectionName == 'instruction' && !isAligner">
        <div v-if="isRequiresShade" class="row">
            <div class="col-6 col-md-4 mb-2">
                <TextInput
                    label="Shade"
                    v-model="instructionSection.shade"
                    :v="v$.instructionSection.shade"
                />
            </div>
        </div>
        <div class="row">
            <div class="col-md-8 mb-2">
                <TextareaInput
                    label="Instructions"
                    v-model="instructionSection.note"
                    :v="v$.instructionSection.note"
                    rows="6"
                />
            </div>
        </div>
        <div class="row">
            <div class="mb-2">
                <AnnotationInput
                    label="Notations"
                    v-model="instructionSection.annotations"
                    :v="v$.instructionSection.annotations"
                />
            </div>
        </div>
    </div>

    <div v-show="currentSectionName == 'instruction'">
        <div class="mb-2">
            <FileInput
                label="Photos"
                v-model="instructionSection.photos"
                :v="v$.instructionSection.photos"
                :allowedExtensions="['jpg', 'jpeg', 'png', 'heic', 'dcm']"
                :isAligner="isAligner"
                :imageUploadGuide="{title: 'The following images should be included in your order',
                images:[{ src: '/resources/image-upload-guide/aligner/teeth-front.webp', alt: 'Teeth front' },
            { src: '/resources/image-upload-guide/aligner/teeth-lh.webp', alt: 'Teeth left side' },
            { src: '/resources/image-upload-guide/aligner/teeth-rh.webp', alt: 'Teeth right side' },
            { src: '/resources/image-upload-guide/aligner/teeth-upper.webp', alt: 'Teeth upper' },
            { src: '/resources/image-upload-guide/aligner/teeth-lower.webp', alt: 'Teeth lower' }]}"

            />
            <QrModal
                v-model="instructionSection.photos"
                :v="v$.instructionSection.photos"
            />
            <div v-if="isAligner == true" class="mb-2 mt-2">
            <p>
                <waiver
                    label="waiver"
                    v-model="instructionSection.waiver"
                    :v="v$.instructionSection.waiver"
                    type="checkbox"
                    id="disclaimer"
                />
            </p>
            </div>
        </div>
        <div v-if="currentSectionName == 'instruction' && isAligner">
            <ToothChart v-bind="{ teethChartAnnotations:instructionSection.teethChartAnnotations}" ></ToothChart>
            <div class="row">
                <div class="col-sm-6 col-md-4 mb-2">
                    <DropdownInput
                        label="Treatment Type"
                        v-model="instructionSection.treatment_type"
                        :v="v$.instructionSection.treatment_type"
                        :options="instructionSection.treatment_types"
                        remove-empty-option="true"
                    />
                </div>
                <div class="col-sm-6 col-md-4 mb-2">
                    <DropdownInput
                        label="Arches"
                        v-model="instructionSection.arch"
                        :v="v$.instructionSection.arch"
                        :options="instructionSection.arches"
                        remove-empty-option="true"
                    />
                </div>
                <div class="col-sm-6 col-md-4 mb-2">
                    <DropdownInput
                        label="Trim Line"
                        v-model="instructionSection.trim_line"
                        :v="v$.instructionSection.trim_line"
                        :options="instructionSection.trim_lines"
                        remove-empty-option="true"
                    />
                </div>
                <div class="col-sd-6 col-md-4 mb-2">
                <DropdownInput
                    label="IPR"
                    v-model="instructionSection.ipr_request"
                    :v="v$.instructionSection.ipr_request"
                    :options="instructionSection.ipr_requested"
                    remove-empty-option="true"
                />
              </div>
              <div class="col-sm-6 col-md-4 mb-2">
                <DropdownInput
                    label="Attachments"
                    v-model="instructionSection.attachment"
                    :v="v$.instructionSection.attachment"
                    :options="instructionSection.attachments_needed"
                    remove-empty-option="true"
                />
              </div>
              
              
            </div>
            <div class="row">
              <div class="col-md-12 mb-2 mt-2">
                <TextareaInput
                    label="Patient Complaints"
                    v-model="instructionSection.complaint"
                    :v="v$.instructionSection.complaint"
                    rows="3"
                />
              </div>
                <div class="col-md-12 mb-2">
                    <TextareaInput
                        label="Other Notes/Instructions"
                        v-model="instructionSection.comment"
                        :v="v$.instructionSection.comment"
                        rows="3"
                    />
                </div>
            </div>
        </div>
        <div class="buttons">
            <button v-if="isAligner == true" class="btn btn-primary me-1" type="submit" :disabled="isSubmitting">Submit</button>
            <button v-if="isAligner == false" class="btn btn-primary me-1" type="button" @click="nextButton">Next</button>
            <button class="btn btn-secondary me-1" type="button" @click="previousButton">Previous</button>
            <router-link class="btn btn-light" to="/orders">Cancel</router-link>
        </div>
    </div>
    <!-- instrutions section ends -->
    <!-- delivey section begins -->
    <div v-if="currentSectionName == 'delivery' && isAligner != true">
        <div class="row">
            <div class="col-sm-12 col-md-5  mb-2">
                <DeliveryDateInput
                    label="Delivery date"
                    v-model="deliverySection.patient_date"
                    :v="v$.deliverySection.patient_date"
                    :account_id="this.accountSection.account_id"
                    :order_type="this.impressionType"
                    :standard="this.basicSection.standard"
                    :pricing_tier="this.basicSection.pricing_tier"
                    :products="this.productsSection.products.map(x => x.id)"
                    @dates="this.dates = dates"
                />
            </div>
        </div>
        <div class="buttons">
            <button v-if="!vouchers || !vouchers.length" class="btn btn-primary me-1" type="submit">Submit</button>
            <button v-if="vouchers && vouchers.length" class="btn btn-primary me-1" type="button" @click="nextButton">Next</button>
            <button class="btn btn-secondary me-1" type="button" @click="previousButton">Previous</button>
            <router-link class="btn btn-light" to="/orders">Cancel</router-link>
        </div>
    </div>
    <!-- delivery section ends -->
    </form>

</template>

<style scoped lang="scss">
    .section-container
    {
        border-bottom: 1px solid $swift-lighter-grey;
    }

    .section
    {
        flex: 1 0 0%;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0px 10px;

        .progress-circle
        {
            display: flex;
            width: 40px;
            height: 40px;
            border-radius: 100px;
            border: 2px solid $swift-fresh-green;
            color: $swift-fresh-green;
            margin-bottom: 5px;
            align-items: center;
            justify-content: center;
            font-weight: bold;
        }
    }

    .section-active
    {
        .progress-circle
        {
            background-color: $swift-fresh-green;
            color: #FFFFFF;
        }
    }

    .impression-type-button
    {
        padding: 40px;
        height: 100%;
        width: 100%;

        .label
        {
            font-size: 1.2em;
        }
    }
</style>

<script>
    import api from "@/services/api";
    import useVuelidate from '@vuelidate/core'
    import {required, requiredIf, minLength, maxLength, helpers} from '@vuelidate/validators'
    import AnnotationInput from '@/components/AnnotationInput.vue';
    import ToothChart from '@/components/ToothChart.vue';
    import TextInput from '@/components/TextInput.vue';
    import TextareaInput from '@/components/TextareaInput.vue';
    import DropdownInput from '@/components/DropdownInput.vue';
    import ProductInput from '@/components/ProductInput.vue';
    import Flash from "@/components/Flash.vue";
    import FileInput from "@/components/FileInput.vue";
    import DeliveryDateInput from '@/components/DeliveryDateInput.vue';

    const todayDate = () => {
        var today = new Date();
        today.setHours(0, 0, 0, 0);

        return new Date(today.setDate(today.getDate()+1)); 
    };

    const rangeEndDate = () => {
        var today = new Date();
        today.setHours(0, 0, 0, 0);

        return new Date(today.setMonth(today.getMonth()+1)); 
    };
    
    const isNotWeekend = (value) => { 
        var day = new Date(value).getUTCDay();

        if([6,0].includes(day)) {
            return false;
        }

        return true;
    };

    const isAfterToday = (value) => {
        var date = new Date(value);
        date.setHours(0, 0, 0, 0);
        return date.getTime() >= todayDate().getTime();
    };

    const isBeforeRangeEnd = (value) => {
        var date = new Date(value);
        date.setHours(0, 0, 0, 0);
        return date.getTime() <= rangeEndDate().getTime()
    };

    const validateReference = (regexParam) => {
        return helpers.withParams({regex: regexParam}, (value) => {
            return regexParam === null || value === null || value.length === 0 || value.match(new RegExp(regexParam)) !== null
        });
    };

    const validateReferralCode = (value) => {
        if (!value || value.length === 0) return true;
        const pattern = /^[A-Z0-9]{4}-?[A-Z0-9]{4}$/i;
        return pattern.test(value);
    };

    const isValidDelivery = (value,siblings) => {
      if(!siblings.delivery_date_invalid){
        return true;
      } else {
        return false;
      }
    };

    const IMPRESSION_TYPE_DIGITAL = 1;
    const IMPRESSION_TYPE_PHYSICAL = 2;
    // const PRICING_TIER_UNSPECIFIED = 0;
    // const STATUS_ACTIVE = 10;
    const SCAN_CONTEXT = 5;
    const PHOTO_CONTEXT = 8;
    const ANNOTATION_CONTEXT = 9;

    export default {
        setup () {
            return {
                v$: useVuelidate()
            }
        },
        props: {
            id: {
                required: true
            },
        },
        data() {
            return {
                productData: null,
                orderData: null,
                accounts: [],
                standards: [],
                pricing_tiers: [],
                currentSectionIndex: 0,
                impressionType: null,
                isEconomyEnabled: false,
                flashMessage: '',
                isSubmitting: false,
                dates: null,
                preferences: {
                    showPatientName: true,
                    patientRefRegex: null,
                    patientExplainer : ''
                },
                accountSection: {
                    account_id: null,
                },
                basicSection: {
                    first_name: '',
                    last_name: '',
                    patient_reference: '',
                    standard: '',
                    referral_code: '',
                },
                productsSection: {
                    products: [],
                },
                scansSection: {
                    scans: [],
                    originalScans: [],
                },
                instructionSection: {
                    shade: '',
                    note: '',
                    annotations: [],
                    photos: [],
                    treatment_types: [],
                    treatment_type: 3,
                    arches: [],
                    arch: 0,
                    ipr_requested: [],
                    ipr_request: 2,
                    attachments_needed: [],
                    attachment: 2,
                    comment:'',
                    complaint: '',
                    teethChartAnnotations: [],
                    trim_lines: [],
                    trim_line: 0,
                    waiver: false,
                }, 
                deliverySection: {
                    patient_date: '',
                    delivery_date_invalid: null,
                },
            }
        },
        computed: {
            sections() {
                var sections = [
                    {
                        name: 'impressions',
                        label: 'Impressions',
                        validation: 'impressionsSection',
                    },
                    {
                        name: 'scans',
                        label: 'Scans',
                        validation: 'scansSection',
                    },
                    {
                        name: 'account',
                        label: 'Account',
                        validation: 'accountSection',
                    },
                    {
                        name: 'basic',
                        label: 'Patient',
                        validation: 'basicSection',
                    },
                    {
                        name: 'products',
                        label: 'Products',
                        validation: 'productsSection',
                    },
                    {
                        name: 'instruction',
                        label: 'Instructions',
                        validation: 'instructionSection',
                    },
                    {
                        name: 'delivery',
                        label: 'Delivery',
                        validation: 'deliverySection',
                    },
                ];

                if(this.impressionType == IMPRESSION_TYPE_PHYSICAL) // Physical
                {
                    sections = sections.filter(section => !['scans'].includes(section.name))
                }

                return sections;
            },
            currentSection() {
                return this.sections[this.currentSectionIndex];
            },
            currentSectionName() {
                return this.currentSection.name;
            },
            isRequiresShade() {
                return this.productsSection.products.filter(product => product.requires_shade).length > 0;
            },
            isAligner() {
                return this.productsSection.products.filter(product => product.is_aligner).length > 0;
            },
        },
        validations () {
            return {
                scansSection: {
                    scans: { required },
                },
                accountSection: {
                    account_id: { required },
                },
                basicSection: {
                    first_name: { requiredIf: requiredIf(!this.basicSection.last_name && !this.basicSection.patient_reference) },
                    last_name: { requiredIf: requiredIf(!this.basicSection.first_name && !this.basicSection.patient_reference) },
                    patient_reference: {
                        requiredIf: requiredIf(!this.basicSection.first_name && !this.basicSection.last_name),
                        mustBeValidReference: helpers.withMessage('Reference is Invalid', validateReference(this.preferences.patientRefRegex)),
                    },
                    standard: { required },
                    pricing_tier: { 
                        requiredIf: requiredIf(this.isEconomyEnabled),
                    },
                    referral_code: {
                        validateReferralCode: helpers.withMessage(
                            'Referral code must be in format XXXX-XXXX or XXXXXXXX',
                            validateReferralCode
                        ),
                    },
                },
                productsSection: {
                    products: { required },
                },
                instructionSection: {
                    shade: { minLength: minLength(1), maxLength: maxLength(15), requiredIf: requiredIf(this.isRequiresShade) },
                    note: { maxLength: maxLength(4000) },
                    annotations: { maxLength: maxLength(100), },
                    photos: {  },
                },
                deliverySection: {
                    patient_date: {
                        required,
                        isNotWeekend: helpers.withMessage('Date cannot be a weekend', isNotWeekend),
                        isAfterToday: helpers.withMessage('Date cannot be in the past', isAfterToday),
                        isBeforeRangeEnd: helpers.withMessage('Date must be in the next two months', isBeforeRangeEnd),
                        isValidDelivery: helpers.withMessage('Date is too soon', isValidDelivery),
                    },
                },
            }
        },
        mounted () {
            this.fetchAccounts();
            this.fetchStandards();
            this.fetchFilesData();    
            this.fetchOrderData();
            this.fetchPracticePrefs();
            
        },
        methods: {
            fullName: function (account) {
                return account.dentist.first_name + ' ' + account.dentist.surname + ' (' + account.code + ')';
            },
            fetchAccounts: function() {
                api
                .get('/api/v1/accounts', {
                    params: {
                        practice_id: this.$store.state.currentPracticeId,
                        bookingOnCheck: true,
                    }
                })
                .then((response) => {
                    this.accounts = response.data.data.map((account) => { return {
                        id: account.id,
                        label: this.fullName(account),
                        is_economy_enabled: account.is_economy_enabled,
                        status: account.status,
                    }});
                    if (this.accounts.length < 1)
                    {
                        alert(
                            'There are no accounts that can create an order' +
                            '\n' +
                            'If you think this is a mistake please contact us on 01204 323 323'
                        );
                    }
                })
                .catch(() => {
                    console.log('Catch accounts error');
                });
            },
            fetchStandards: function() {                
                this.standards = [{
                    id: 1,
                    label: 'NHS / Standard',
                }, {
                    id: 2,
                    label: 'Independent',
                }, {
                    id: 3,
                    label: 'Private',
                }]
            },
            fetchOrderData: function() {                
                api
                .get('/api/v1/orders/' + this.id)
                .then((response) => {
                    this.orderData = response.data.data;
                    this.stageData = response.data.data.stages;
                    this.presetValues();
                })
                .catch(() => {
                    console.log('Catch order error');
                });
            },
            fetchFilesData: function() {                
                api
                .get('/api/v1/orders/' + this.id + '/files')
                .then((response) => {
                    this.filesData = response.data.data;
                })
                .catch(() => {
                    console.log('Catch files error');
                });
            },
            nextButton: async function() {
                var currentSectionValidator = this.currentSection.validation;
                
                var isValid = await this.v$[currentSectionValidator].$validate();

                if(isValid)
                {
                    this.currentSectionIndex++;
                }
            },
            impressionTypeButton: function(impressionType) {
                this.impressionType = impressionType;
                this.currentSectionIndex++;
            },
            previousButton: async function() {
                this.currentSectionIndex--;

                if(this.currentSectionName === 'impressions')
                {
                    this.impressionType = null;
                }
            },
            presetValues: function () {
                this.accountSection.account_id = this.orderData.account.id;
                this.basicSection.patient_reference = this.orderData.patient_name
                this.basicSection.standard = this.orderData.standard.id
                this.instructionSection.shade = this.orderData.shade;
                this.instructionSection.note = this.orderData.note;
                this.basicSection.pricing_tier = this.orderData.stages[0].pricing_tier;

                this.setPhotos();

                this.setScans();
            },
            setPhotos()
            {
                this.filesData.forEach(file => {
                    if (file.context_tag == SCAN_CONTEXT || file.context_tag == PHOTO_CONTEXT || file.context_tag == ANNOTATION_CONTEXT) {
                        this.instructionSection.photos.push(file);
                    }}
                )
            },
            setScans: function()
            {
                this.filesData.forEach(file => {
                    if (file.context_tag == SCAN_CONTEXT) {
                        this.scansSection.originalScans.push(file);
                    }}
                )
            },
            fetchPracticePrefs: function () {
                api
                    .get('/api/v1/practices/' + this.$store.state.currentPracticeId + '/preferences', {
                    })
                    .then((response) => {
                        this.preferences = {
                            showPatientName: response.data.data.providing_patient_names,
                            patientRefRegex: response.data.data.patient_ref_regex,
                            patientExplainer: response.data.data.patient_ref_explainer,
                        };
                    })
                    .catch((reason) => {
                        console.log('Catch practice preferences error');
                        console.log(reason);
                    });
            },
            setProductData: function(productId)
            {
                if (this.$refs.remakeProduct.products.length > 0) {
                    return; // Product data already set, no need to fetch again
                }
                
                api
                .get('/api/v1/products/' + productId, {
                    params: {
                        practice_id: this.$store.state.currentPracticeId,
                    }
                })
                .then((response) => {
                    // product.materials = response.data.materials;
                    // this.productSelection = product;
                    switch(parseInt(this.service_level))
                    {
                        case 1: this.material = response.data.default_materials.nhs; break;
                        case 2: this.material = response.data.default_materials.ind; break;
                        case 3: this.material = response.data.default_materials.prv; break;
                        default: this.material = null; break;
                    }

                    this.productData = response.data;

                    this.$refs.remakeProduct.products.push({
                            id: this.orderData.stages[0].products[0].entity_id,
                            name: this.orderData.stages[0].products[0].name,
                            quantity: this.orderData.stages[0].products[0].quantity,
                            material_id: this.material,
                            requires_shade: this.productData.requires_shade,
                        })
                })
                .catch(() => {
                    console.log('Catch product error');
                }); 

            },
            submitOrderRemake: async function () {

                if (this.isSubmitting) {
                    return; // If already submitting, exit the function
                }

                this.isSubmitting = true;

                var currentSectionValidator = this.currentSection.validation;
                this.deliverySection.delivery_date_invalid = null;
                await this.v$[currentSectionValidator].$reset();
                var isValid = await this.v$[currentSectionValidator].$validate();
                if(isValid)
                {
                    this.requestOrderCreation();
                }
                else
                {
                    console.log('Validation errors', this.v$.$errors.map((error) => { return error.$property + ': ' + error.$message}));
                    this.isSubmitting = false;
                }
            },
            requestOrderCreation: function () {

                api.post('/api/v1/orders', this.orderRequestData())
                .then((response) => {
                    if(this.impressionType == IMPRESSION_TYPE_PHYSICAL)
                    {
                        this.$router.push({ name: 'OrderCreateSend', params: { 'id': response.data.id }});
                    }
                    else
                    {
                        this.$router.push({ name: 'PendingOrders' });
                    }
                })
                .catch((error) => {
                    this.flashMessage = this.getErrorMessage(error, 'Failed to create order, please try again later.');
                });
            },
            getErrorMessage(error = null, defaultMessage) {
                this.scrollToTop();
                if (error.response?.data?.message) {
                    return error.response.data.message + '. ' + defaultMessage;
                }
                if (error.message) {
                    return error.message + '. ' + defaultMessage;
                }
                return defaultMessage;
            },
            scrollToTop() {
                window.scrollTo({ 
                    top: 0, 
                    behavior: 'smooth' 
                });
            },
            orderRequestData: function() {
                var data = {
                    order_type: this.impressionType,
                    account_id: this.accountSection.account_id,
                    first_name: this.basicSection.first_name,
                    last_name: this.basicSection.last_name,
                    patient_reference: this.basicSection.patient_reference,
                    standard: this.basicSection.standard,
                    pricing_tier: this.basicSection.pricing_tier,
                    referral_code: this.basicSection.referral_code ? this.basicSection.referral_code.replace(/-/g, '').toUpperCase() : this.basicSection.referral_code,
                    patient_date: this.deliverySection.patient_date,
                    note: this.instructionSection.note,
                    aligner_detail: {
                        treatment_type: this.instructionSection.treatment_type,
                        arch: this.instructionSection.arch,
                        attachment: this.instructionSection.attachment,
                        ipr_request: this.instructionSection.ipr_request,
                        comment: this.instructionSection.comment,
                        complaint: this.instructionSection.complaint,
                        trim_line: this.instructionSection.trim_line,
                    },
                    teeth_chart: this.instructionSection.teethChartAnnotations,
                    products: this.productsSection.products.map((product) => {
                        return {
                            id: product.id,
                            quantity: product.quantity,
                            material_id: product.material_id,
                        }
                    }),
                    is_aligner: this.isAligner,
                    is_remake: true,
                    original_order_id: this.id,

                };

                data.originalScans = this.scansSection.originalScans;

                // Supply scans data if digital impressions selected. 
                if(this.impressionType == IMPRESSION_TYPE_DIGITAL)
                {
                    data.scans = this.scansSection.scans.map(scan => scan.hash);
                }

                if(this.instructionSection.shade)
                {
                    data.shade = this.instructionSection.shade;
                }

                if(this.instructionSection.photos)
                {
                    data.files = this.instructionSection.photos.map(photo => photo.hash);
                }

                if(this.instructionSection.annotations)
                {
                    data.annotations = this.instructionSection.annotations.map((annotation) => {
                        var annotationReturn = {
                            file_id: annotation.hash,
                        };

                        if(annotation.text.length)
                        {
                            annotationReturn.text = annotation.text;
                        }

                        return annotationReturn;
                    });
                }

                return data;
            },
        },
        watch: {
            currentSection(){
                console.log(this.currentSection.name)
                if (this.currentSection.name == 'products') {
                    this.$nextTick(function () {
                        // get product deets and populate?
                        // service level, account id, product id
                        let product = {};
                        product.id = this.orderData.stages[0].products[0].entity_id;

                        this.setProductData(product.id);
                    })
                }
            }
        },
        components: {
            TextInput,
            DropdownInput,
            FileInput,
            ProductInput,
            TextareaInput,
            DeliveryDateInput,
            Flash,
            AnnotationInput,
            ToothChart,
        },
        
    }

</script>